import React from "react";
import { Grid, Button, TextField, FormControl } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormSelectField from "components/FormFields/FormSelect";
import FormTextField from "components/FormFields/FormTextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import SubmitButton from "components/Button/SubmitButton";
import FormDatePicker from "components/FormFields/FormDatePicker";
import ConfirmModal from "components/common/modals/confirmModal/ConfirmModal";
import { getFormattedDate, isValidDate } from "utils/date";
import { CHANGE_DEVICE_ALERT } from "static/constants";
import { checkIsAlphaNumeric } from "utils/string";
import useAddNewDeviceForm from "./useAddNewDeviceForm";
import dayjs from "dayjs";
import { DATE_FORMAT } from "static/date";

const AddNewDeviceForm = (props) => {
  const {
    deviceList,
    accountList,
    showConfirmModal,
    manufacturersList,
    deviceFields: { account, manufacturer, doi, modelNumber, modelName },

    onUpdate,
    onFormSubmit,
    setShowConfirmModal,
    handleFormSubmitConfirm,
  } = useAddNewDeviceForm(props);

  // Yup validation schema
  const validationSchema = Yup.object({
    account: Yup.string().required("Account is required"),
    manufacturer: Yup.string().required("Manufacturer is required"),
    modelName: Yup.string().required("Model Name is required"),
    modelNumber: Yup.string()
      .required("Serial Number is required")
      .matches(/^[a-zA-Z0-9]*$/, "Serial Number must be alphanumeric"),
    doi: Yup.string()
      .required("Date of Implant is required")
      .test("is-valid-date", "Date of Implant must be a valid date", (value) =>
        isValidDate(value)
      ),
  });

  const filter = createFilterOptions();

  return (
    <Formik
      initialValues={{
        account: "",
        manufacturer: "",
        modelName: "",
        modelNumber: "",
        doi: null,
      }}
      validationSchema={validationSchema}
      onSubmit={onFormSubmit}
      enableReinitialize
      initialErrors={{}}
    >
      {({ setFieldValue, isSubmitting, handleSubmit, errors, touched }) => (
        <Form>
          <Grid container spacing={2} className="mb-3" s>
            <Grid item xs={12}>
              <FormSelectField
                label="Account"
                required
                value={account}
                margin="none"
                options={accountList ?? []}
                hasError={touched.account && Boolean(errors.account)}
                errorMessage={touched.account && errors.account}
                onChange={(value) => {
                  onUpdate("account", value);
                  setFieldValue("account", value);
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormSelectField
                margin="none"
                required
                value={manufacturer}
                label="Manufacturer"
                options={manufacturersList ?? []}
                hasError={touched.manufacturer && Boolean(errors.manufacturer)}
                errorMessage={touched.manufacturer && errors.manufacturer}
                onChange={(value) => {
                  onUpdate("manufacturer", value);
                  setFieldValue("manufacturer", value);
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl margin="none" fullWidth>
                <Autocomplete
                  name="modelName"
                  fullWidth
                  clearOnBlur
                  selectOnFocus
                  handleHomeEndKeys
                  id="free-solo-with-text-demo"
                  value={
                    deviceList?.find(
                      (item) => item?.modelDisplayName === modelName
                    ) || null
                  }
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    return filtered;
                  }}
                  options={
                    deviceList?.filter((item) => item?.modelDisplayName) || []
                  }
                  getOptionLabel={(option) => option?.modelDisplayName}
                  onChange={(_event, { modelDisplayName }) => {
                    onUpdate("modelName", modelDisplayName);
                    setFieldValue("modelName", modelDisplayName);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      error={touched.modelName && Boolean(errors.modelName)}
                      helperText={touched.modelName && errors.modelName}
                      label="Model Name"
                      variant="outlined"
                      placeholder="Model Name"
                      margin="none"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormTextField
                name="modelNumber"
                fullWidth
                required
                label="Serial Number"
                margin="none"
                inputProps={{ maxLength: 50 }}
                value={modelNumber}
                hasError={touched.modelNumber && Boolean(errors.modelNumber)}
                errorMessage={touched.modelNumber && errors.modelNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || checkIsAlphaNumeric(value)) {
                    setFieldValue("modelNumber", value);
                    onUpdate("modelNumber", value);
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} style={{ boxSizing: "border-box" }}>
              <FormControl fullWidth>
                <FormDatePicker
                  name="doi"
                  required
                  maxDate={dayjs(new Date())}
                  label="Date of Implant"
                  submitted={touched.doi}
                  value={doi ? dayjs(doi) : null}
                  hasError={touched.doi && Boolean(errors.doi)}
                  errorMessage={touched.doi && errors.doi}
                  onChange={(date) => {
                    const formattedDate = getFormattedDate(
                      date,
                      DATE_FORMAT.DEFAULT
                    );
                    setFieldValue("doi", formattedDate);
                    onUpdate("doi", formattedDate);
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>

          <div className="d-flex flex-end" style={{ gap: "10px" }}>
            <Button
              color="secondary"
              variant="outlined"
              onClick={props.onClose}
            >
              Cancel
            </Button>

            <SubmitButton handleClick={handleSubmit} />
          </div>

          {showConfirmModal && (
            <ConfirmModal
              confirmText="Confirm"
              title="Change Device"
              show={showConfirmModal}
              isConfirming={isSubmitting}
              onConfirm={handleFormSubmitConfirm}
              onCancel={() => setShowConfirmModal(false)}
              children={
                <span>
                  {CHANGE_DEVICE_ALERT} <b>{props?.currentDevice}</b>. Are you
                  sure you want to confirm this change?
                </span>
              }
            />
          )}
        </Form>
      )}
    </Formik>
  );
};

export default AddNewDeviceForm;
